import styled from '@emotion/styled';
import { PublicLayout } from '../../components/PublicLayout';

const PageHeader = styled.section`
  padding: ${({ theme }) => theme.spacing(14)} ${({ theme }) => theme.spacing(8)};
  background: ${({ theme }) => theme.background.secondary};
  border-bottom: 1px solid ${({ theme }) => theme.border.color.light};
`;

const Title = styled.h1`
  font-size: clamp(26px, 3.5vw, 40px);
  font-weight: 700;
  letter-spacing: -0.5px;
  color: ${({ theme }) => theme.font.color.primary};
  margin: 0 0 ${({ theme }) => theme.spacing(3)} 0;
  max-width: 860px;
`;

const LastUpdated = styled.p`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.font.color.tertiary};
  margin: 0;
`;

const Content = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing(14)} ${({ theme }) => theme.spacing(8)};
`;

const Section = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing(12)};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.font.size.lg};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.primary};
  margin: 0 0 ${({ theme }) => theme.spacing(4)} 0;
`;

// Smaller legal-section subheading. Used inside a Section to break up
// long bodies of legal text. Replaces a repeated inline-style pattern.
const SubheadingTitle = styled.h3`
  font-size: 15px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.primary};
  margin: ${({ theme }) => theme.spacing(6)} 0 ${({ theme }) => theme.spacing(4)} 0;
`;

const Para = styled.p`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.font.color.secondary};
  line-height: 1.8;
  margin: 0 0 ${({ theme }) => theme.spacing(4)} 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const BulletList = styled.ul`
  padding-left: ${({ theme }) => theme.spacing(6)};
  margin: 0 0 ${({ theme }) => theme.spacing(4)} 0;
`;

const BulletItem = styled.li`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.font.color.secondary};
  line-height: 1.8;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.border.color.light};
  margin: ${({ theme }) => theme.spacing(12)} 0;
`;

const ContactLink = styled.a`
  color: ${({ theme }) => '#0d253d'};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

function Privacy() {
  return (
    <PublicLayout>
      <PageHeader>
        <Title>Privacy Policy &amp; Terms of Use</Title>
        <LastUpdated>Last updated: January 2025</LastUpdated>
      </PageHeader>

      <Content>
        <Section>
          <SectionTitle>Overview</SectionTitle>
          <Para>
            This document describes how Simkura collects, uses, and protects information provided through the Simkura platform ("the Platform"), and the terms under which the Platform is made available to users and partners.
          </Para>
          <Para>
            By accessing or using the Platform, you agree to the terms set out in this document. If you have questions, please contact us at <ContactLink href="mailto:info@simkura.com">info@simkura.com</ContactLink>.
          </Para>
        </Section>

        <Divider />

        <Section>
          <SectionTitle>Privacy Policy</SectionTitle>

          <SubheadingTitle>Information we collect</SubheadingTitle>
          <Para>
            To operate the Platform, we collect and process the following categories of information:
          </Para>
          <BulletList>
            <BulletItem><strong>Account information</strong> — name, email address, and hashed password for user authentication.</BulletItem>
            <BulletItem><strong>Company information</strong> — organisation name and billing details for multi-tenant account management.</BulletItem>
            <BulletItem><strong>Device telemetry</strong> — status, event logs, and health metrics received from connected Simkura devices.</BulletItem>
            <BulletItem><strong>Access events</strong> — credential access grants and denials, timestamped and associated with a device and company.</BulletItem>
            <BulletItem><strong>Integration configuration</strong> — API keys, webhook URLs, and MQTT credentials created by users.</BulletItem>
            <BulletItem><strong>Usage logs</strong> — API requests, authentication events, and audit logs used for security and troubleshooting.</BulletItem>
          </BulletList>

          <SubheadingTitle>How we use your information</SubheadingTitle>
          <Para>
            Information collected is used solely to operate and improve the Platform. Specifically:
          </Para>
          <BulletList>
            <BulletItem>To authenticate users and enforce role-based access control.</BulletItem>
            <BulletItem>To display device status, events, and fleet health information to authorised users.</BulletItem>
            <BulletItem>To deliver webhook notifications to endpoints configured by your company.</BulletItem>
            <BulletItem>To calculate usage for billing purposes (device counts per billing period).</BulletItem>
            <BulletItem>To provide support and investigate reported issues.</BulletItem>
          </BulletList>
          <Para>
            We do not sell, rent, or share your personal information with third parties for marketing purposes.
          </Para>

          <SubheadingTitle>Data retention</SubheadingTitle>
          <Para>
            Device event logs are retained for 30 days by default and then automatically purged. Account and company data is retained for the duration of your subscription and deleted within 90 days of account closure upon request.
          </Para>

          <SubheadingTitle>Security</SubheadingTitle>
          <Para>
            All data in transit is encrypted using TLS 1.2 or higher. Passwords are stored as one-way hashes using bcrypt. API keys and webhook secrets are stored as SHA-256 hashes and are never logged in plaintext. Access to production systems is restricted to authorised personnel only.
          </Para>

          <SubheadingTitle>Third-party services</SubheadingTitle>
          <Para>
            The Platform integrates with the following third-party services to operate:
          </Para>
          <BulletList>
            <BulletItem><strong>Cloud connectivity provider</strong> — secure cellular device messaging and MQTT routing.</BulletItem>
            <BulletItem><strong>Stripe</strong> — payment processing and metered billing (where enabled).</BulletItem>
            <BulletItem><strong>Sentry</strong> — error monitoring and crash reporting.</BulletItem>
          </BulletList>
          <Para>
            Each of these services operates under its own privacy policy. Device event data shared with our connectivity provider is limited to what is necessary for message routing.
          </Para>
        </Section>

        <Divider />

        <Section>
          <SectionTitle>Terms of Use</SectionTitle>

          <SubheadingTitle>Permitted use</SubheadingTitle>
          <Para>
            The Platform is made available to OEM partners and their customers for the purpose of managing Simkura-connected devices. Use of the Platform for any other purpose, or in any way that violates applicable law, is prohibited.
          </Para>

          <SubheadingTitle>API and integration use</SubheadingTitle>
          <Para>
            API keys and integration credentials issued through the Platform are for your use only and must not be shared. You are responsible for all activity that occurs under your credentials. Keys should be rotated if you suspect compromise.
          </Para>
          <Para>
            Rate limits are enforced to protect platform stability. Automated systems must implement exponential backoff when receiving <code>429</code> responses.
          </Para>

          <SubheadingTitle>Service availability</SubheadingTitle>
          <Para>
            We aim to maintain high availability but do not guarantee uninterrupted service. Scheduled maintenance will be communicated in advance where possible. The Platform includes circuit breakers and retry mechanisms for resilience against transient failures.
          </Para>

          <SubheadingTitle>Limitation of liability</SubheadingTitle>
          <Para>
            Simkura shall not be liable for any indirect, incidental, or consequential damages arising from use of or inability to use the Platform. Our total liability for any claim shall not exceed the fees paid by you in the preceding three months.
          </Para>

          <SubheadingTitle>Changes to these terms</SubheadingTitle>
          <Para>
            We may update this document from time to time. Material changes will be communicated by email to account holders. Continued use of the Platform after changes take effect constitutes acceptance of the updated terms.
          </Para>
        </Section>

        <Divider />

        <Section>
          <SectionTitle>Contact</SectionTitle>
          <Para>
            For privacy requests, data deletion, or questions about these terms, contact us at <ContactLink href="mailto:info@simkura.com">info@simkura.com</ContactLink>.
          </Para>
        </Section>
      </Content>
    </PublicLayout>
  );
}

export default Privacy;
